import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import type { CalendarEvent } from '../types';
import { styles } from '../styles';
import { DayEvents } from './DayEvents';
import { SkeletonLoader } from './SkeletonLoader';

interface DayGridProps {
  date: Date;
  dayEvents: CalendarEvent[];
  today: boolean;
  disabled?: boolean;
  borderColor?: string;
  dayNumberColor?: string;
  disabledDayColor?: string;
  todayAccentColor?: string;
  disabledBackgroundColor?: string;
  cellBackgroundColor?: string;
  overflowBadgeBackgroundColor?: string;
  overflowBadgeTextColor?: string;
  onDatePress: (day: number) => void;
  onEventDetailsPress?: (event: CalendarEvent) => void;
  onEventPress?: (event: CalendarEvent) => void;
  onShowAllEvents?: () => void;
  renderEventBadge: (event: CalendarEvent, small?: boolean) => React.ReactNode;
  variant?: 'monthly' | 'weekly-mobile' | 'weekly-web';
  isLoading?: boolean;
}

/**
 * DayGrid Component
 * Reusable component for rendering individual day cells
 * Used by both MonthlyView and WeeklyView
 */
export const DayGrid: React.FC<DayGridProps> = ({
  date,
  dayEvents,
  today,
  disabled = false,
  borderColor,
  dayNumberColor,
  disabledDayColor,
  todayAccentColor = '#034895',
  disabledBackgroundColor = '#fafafa',
  cellBackgroundColor,
  overflowBadgeBackgroundColor,
  overflowBadgeTextColor,
  onDatePress,
  onEventDetailsPress,
  onEventPress,
  onShowAllEvents,
  renderEventBadge,
  variant = 'monthly',
  isLoading = false,
}) => {
  const dayNumber = date.getDate();
  const isWeeklyWeb = variant === 'weekly-web';
  const isWeeklyMobile = variant === 'weekly-mobile';

  // Render skeleton loading state
  if (isLoading) {
    return (
      <View
        style={[
          isWeeklyWeb ? styles.weeklyDayCellWeb : (isWeeklyMobile ? styles.weeklyDayCell : styles.dayCell),
          { backgroundColor: cellBackgroundColor },
          { borderColor },
          disabled && !today && { backgroundColor: disabledBackgroundColor },
          { 
            flexDirection: 'column', 
            justifyContent: 'flex-start',
            overflow: 'hidden',
          },
        ]}
      >
        <View style={[
          isWeeklyWeb ? styles.weeklyDateSectionWeb : (isWeeklyMobile ? styles.weeklyDateSection : styles.dayHeader),
          { flexShrink: 0 },
        ]}>
          <SkeletonLoader
            variant="day-number"
            width={24}
            height={24}
            borderRadius={12}
          />
        </View>
        <View style={{ flex: 1, minHeight: 0, paddingTop: 4, gap: 4 }}>
          <SkeletonLoader
            variant="event-badge"
            width="100%"
            height={20}
            borderRadius={4}
          />
          <SkeletonLoader
            variant="event-badge"
            width="85%"
            height={20}
            borderRadius={4}
          />
        </View>
      </View>
    );
  }

  return (
    <TouchableOpacity
      style={[
        isWeeklyWeb ? styles.weeklyDayCellWeb : (isWeeklyMobile ? styles.weeklyDayCell : styles.dayCell),
        { backgroundColor: cellBackgroundColor },
        { borderColor },
        disabled && !today && { backgroundColor: disabledBackgroundColor },
        { 
          flexDirection: 'column', 
          justifyContent: 'flex-start',
          overflow: 'hidden',
        },
      ]}
      onPress={() => onDatePress(dayNumber)}
    >
      <View style={[
        isWeeklyWeb ? styles.weeklyDateSectionWeb : (isWeeklyMobile ? styles.weeklyDateSection : styles.dayHeader),
        { flexShrink: 0 },
      ]}>
        {today ? (
          <View style={{ position: 'relative', alignItems: 'center' }}>
            <Text
              style={[
                isWeeklyWeb ? styles.weeklyDateNumberWeb : (isWeeklyMobile ? styles.weeklyDateNumber : styles.dayNumber),
                {
                  color: todayAccentColor,
                },
                isWeeklyMobile ? styles.weeklyDateNumberToday : (isWeeklyWeb ? styles.weeklyDateNumberToday : styles.todayNumber),
              ]}
            >
              {dayNumber}
            </Text>
            <View
              style={{
                position: 'absolute',
                bottom: -6,
                width: 4,
                height: 4,
                backgroundColor: todayAccentColor,
              }}
            />
          </View>
        ) : (
          <Text
            style={[
              isWeeklyWeb ? styles.weeklyDateNumberWeb : (isWeeklyMobile ? styles.weeklyDateNumber : styles.dayNumber),
              {
                color: disabled ? disabledDayColor : dayNumberColor,
              },
            ]}
          >
            {dayNumber}
          </Text>
        )}
      </View>
      {dayEvents.length > 0 ? (
        <View style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <DayEvents
            dayEvents={dayEvents}
            renderEventBadge={renderEventBadge}
            onEventDetailsPress={onEventDetailsPress}
            onEventPress={onEventPress}
            onShowAllEvents={onShowAllEvents}
            variant={variant}
            theme={{
              overflowBadgeBackgroundColor,
              overflowBadgeTextColor,
            }}
          />
        </View>
      ) : null}
    </TouchableOpacity>
  );
};

export default DayGrid;
